import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  CreditCard, 
  FileCheck, 
  Trash2, 
  Edit2, 
  Plus, 
  X, 
  AlertCircle, 
  FileText, 
  Mail, 
  Info, 
  Loader, 
  Building, 
  Shield, 
  CheckCircle, 
  Database, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  RefreshCw,
  Settings,
  Target,
  Users,
  BarChart3,
  Layers,
  Globe,
  Server,
  Bike,
  Bug,
  Receipt
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  doc, 
  getDocs,
  getDoc,  // 🔥 ADDED FOR VERIFICATION
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// Import multi-tenant custom components with invoice functionality
import FilterSystem from './FilterSystem';
import ReservationForm from './ReservationForm';
import InquiryHandler from './InquiryHandler';
import ReservationStatistics from './ReservationStatistics';
import ReservationList from './ReservationList';
import SharedInvoicePreviewPage from '../Invoice Management/SharedInvoicePreviewPage';

// Import invoice utility functions
import { createInvoiceFromReservation } from './invoiceUtils';

// Custom Icons
const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);

// FIREBASE BIKE UTILITY FUNCTIONS
const getModelFromBike = (bike) => {
  if (!bike) return 'Unknown';
  
  if (typeof bike === 'object' && bike.model) {
    return bike.model;
  }
  
  if (typeof bike === 'string') {
    const regNumber = bike.trim().toUpperCase();
    return bike;
  }
  
  return 'Unknown';
};

const getRegistrationFromBike = (bike) => {
  if (!bike) return 'Unknown';
  
  if (typeof bike === 'object') {
    return bike.regNumber || bike.registration || bike.reg || 'Unknown';
  }
  
  if (typeof bike === 'string') {
    return bike;
  }
  
  return 'Unknown';
};

// NEW: HELPER FUNCTION TO FIND BIKE AND GET MODEL
const findBikeAndGetModel = (motorbikeNumber, managedBikes) => {
  if (!motorbikeNumber || !managedBikes || managedBikes.length === 0) {
    return { bike: null, model: 'Unknown', regNumber: motorbikeNumber || 'Unknown' };
  }

  console.log('🔍 Finding bike for:', motorbikeNumber);
  
  // Strategy 1: Exact regNumber match (case insensitive)
  let targetBike = managedBikes.find(bike => 
    bike.regNumber?.toLowerCase() === motorbikeNumber?.toLowerCase()
  );
  
  if (targetBike) {
    console.log('✅ Found by exact regNumber:', targetBike.regNumber, 'Model:', targetBike.model);
    return {
      bike: targetBike,
      model: targetBike.model || 'Unknown',
      regNumber: targetBike.regNumber
    };
  }
  
  // Strategy 2: Exact ID match
  targetBike = managedBikes.find(bike => bike.id === motorbikeNumber);
  if (targetBike) {
    console.log('✅ Found by ID:', targetBike.id, 'Model:', targetBike.model);
    return {
      bike: targetBike,
      model: targetBike.model || 'Unknown',
      regNumber: targetBike.regNumber
    };
  }
  
  // Strategy 3: Partial match
  targetBike = managedBikes.find(bike => 
    bike.regNumber?.includes(motorbikeNumber) ||
    motorbikeNumber?.includes(bike.regNumber)
  );
  
  if (targetBike) {
    console.log('✅ Found by partial match:', targetBike.regNumber, 'Model:', targetBike.model);
    return {
      bike: targetBike,
      model: targetBike.model || 'Unknown',
      regNumber: targetBike.regNumber
    };
  }
  
  console.warn('⚠️ No matching bike found for:', motorbikeNumber);
  return { bike: null, model: 'Unknown', regNumber: motorbikeNumber };
};

// SIMPLE FILTERING FUNCTION FOR CONSISTENT BOOKING COUNTS
const filterCompanyReservations = (allReservations, currentCompanyId) => {
  console.log('🔍 Reservations Filtering:', {
    total: allReservations?.length || 0,
    companyId: currentCompanyId
  });

  if (!allReservations || !currentCompanyId) {
    return [];
  }

  const filtered = allReservations.filter(reservation => 
    reservation.companyId === currentCompanyId
  );

  console.log('📊 Reservations Results:', {
    original: allReservations.length,
    filtered: filtered.length,
    excluded: allReservations.length - filtered.length
  });

  return filtered;
};

// MULTI-TENANT TOOLTIP COMPONENT
const InfoTooltip = ({ show, message }) => {
  if (!show) return null;
  
  return (
    <div className="absolute bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-gray-700 w-64 z-10 -mt-16 ml-8">
      <div className="flex items-start gap-2">
        <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm">{message}</p>
      </div>
      <div className="absolute w-3 h-3 bg-gray-900 border-l border-b border-gray-700 transform rotate-45 -ml-1.5 left-0 top-1/2 -mt-1.5"></div>
    </div>
  );
};

// MULTI-TENANT INQUIRY SOURCE BADGE
const InquirySourceBadge = ({ source, companyName }) => {
  return (
    <div className="flex items-center bg-purple-900/40 rounded-full px-2 sm:px-3 py-1 text-xs border border-purple-700">
      {source === 'whatsapp' ? (
        <Phone size={12} className="text-green-400 mr-1" />
      ) : (
        <Mail size={12} className="text-blue-400 mr-1" />
      )}
      <span className="text-purple-200 truncate">
        From {source === 'whatsapp' ? 'WhatsApp' : 'Email'} Inquiry
        {companyName && <span className="ml-1 text-purple-300 hidden sm:inline">• {companyName}</span>}
      </span>
    </div>
  );
};

// MAIN RESERVATIONS COMPONENT WITH INVOICE FUNCTIONALITY
const Reservations = ({ 
  currentUser, 
  currentCompany, 
  getCompanyContext, 
  hasPermission 
}) => {
  const navigate = useNavigate();
  
  // Core State Management
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // NEW: Navigation state for SharedInvoicePreviewPage
  const [currentView, setCurrentView] = useState('reservations');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

  // DEBUG STATE
  const [debugMode, setDebugMode] = useState(process.env.NODE_ENV === 'development');
  const [debugInfo, setDebugInfo] = useState({});

  // Reservation Form Data
  const [reservationData, setReservationData] = useState({
    customerName: '',
    whatsappNumber: '',
    localNumber: '',
    email: '',
    licenseNumber: '',
    pickupDateTime: '',
    dropoffDateTime: '',
    pickupLocation: 'SLBR',
    customPickupLocation: '',
    dropoffLocation: 'SLBR',
    customDropoffLocation: '',
    motorbikeNumber: '',
    bikeModel: '', // NEW: Added bikeModel to form state
    availabilityStatus: 'booked',
    rentalAmount: '',
    rentalPaymentStatus: 'paid',
    depositAmount: '$100',
    drivingLicenseStatus: 'applied',
    drivingLicensePaymentStatus: 'paid',
    licensePickupDate: '',
    licensePickupLocation: 'SLBR',
    customLicensePickupLocation: '',
    note: '',
    isInquiry: false,
    inquirySource: '',
    companyId: '',
    companyName: '',
    createdBy: '',
    createdByUsername: ''
  });

  // Data State
  const [editIndex, setEditIndex] = useState(null);
  const [editingReservationId, setEditingReservationId] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [licensePickupDate, setLicensePickupDate] = useState("");
  const [filteredPickupReservations, setFilteredPickupReservations] = useState([]);
  const [filteredDropoffReservations, setFilteredDropoffReservations] = useState([]);
  const [bikePickupDate, setBikePickupDate] = useState("");
  const [bikeDropoffDate, setBikeDropoffDate] = useState("");
  const [displayedReservations, setDisplayedReservations] = useState([]);
  
  // FIREBASE MANAGED BIKES STATE
  const [managedBikes, setManagedBikes] = useState([]);
  const [bikesLoading, setBikesLoading] = useState(true);
  const [bikesError, setBikesError] = useState(null);
  
  // INVOICE STATE - NEW
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(null);
  
  // UI State
  const [showInquiries, setShowInquiries] = useState(true);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipIndex, setTooltipIndex] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Get company context
  const companyId = getCompanyContext();

  // NEW: Navigation handlers for SharedInvoicePreviewPage
  const handleNavigateToInvoicePreview = (data) => {
    console.log('🧾 Navigating to invoice preview from reservations:', data);
    setSelectedInvoiceData(data);
    setCurrentView('invoice-preview');
  };

  const handleNavigateBackToReservations = () => {
    console.log('🔙 Navigating back to reservations');
    setCurrentView('reservations');
    setSelectedInvoiceData(null);
  };

  // DEBUG FUNCTION
  const updateDebugInfo = (key, value) => {
    if (debugMode) {
      setDebugInfo(prev => ({
        ...prev,
        [key]: value,
        lastUpdate: new Date().toLocaleTimeString()
      }));
      console.log(`🐛 RESERVATIONS DEBUG [${key}]:`, value);
    }
  };

  // FIREBASE COLLECTIONS
  const getCompanyCollectionPath = useCallback((collection) => {
    if (!companyId) {
      console.error('❌ No company context available for collection:', collection);
      return null;
    }
    return `companies/${companyId}/${collection}`;
  }, [companyId]);

  // Firebase Collections
  const reservationsCollection = useMemo(() => {
    const path = getCompanyCollectionPath('reservations');
    return path ? collection(db, path) : null;
  }, [getCompanyCollectionPath]);

  const inquiriesCollection = useMemo(() => {
    const path = getCompanyCollectionPath('inquiries');
    return path ? collection(db, path) : null;
  }, [getCompanyCollectionPath]);

  const motorbikeRentalsCollection = useMemo(() => {
    const path = getCompanyCollectionPath('motorbike-rentals');
    return path ? collection(db, path) : null;
  }, [getCompanyCollectionPath]);

  // INVOICES COLLECTION - NEW
  const invoicesCollection = useMemo(() => {
    const path = getCompanyCollectionPath('invoices');
    return path ? collection(db, path) : null;
  }, [getCompanyCollectionPath]);

  // FIREBASE MANAGED BIKES COLLECTION
  const managedBikesCollection = useMemo(() => {
    return collection(db, 'managedBikes');
  }, []);

  // UTILITY FUNCTIONS
  const getModelRateWithFallback = (bikeModel) => {
    const fallbackRates = {
      'DIO': 25, 'APRILIA': 30, 'XPULSE': 35, 'XBLADE': 30, 'HORNET': 35,
      'SHINE': 25, 'NAVI': 20, 'TRIGGER': 25, 'NTORQ': 25, 'HONDA CD': 25, 'XR': 30,
      'DEFAULT': 25
    };
    
    return fallbackRates[bikeModel?.toUpperCase()] || fallbackRates['DEFAULT'];
  };

  // LOAD MANAGED BIKES FROM FIREBASE
  const loadManagedBikes = async () => {
    if (!companyId) {
      console.warn('No company context for loading managed bikes');
      setBikesError('No company context available');
      setBikesLoading(false);
      return;
    }

    try {
      setBikesLoading(true);
      setBikesError(null);
      console.log('🚴 Loading managed bikes for company:', companyId);
      
      const q = query(managedBikesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const loadedBikes = [];
      
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        loadedBikes.push(bikeData);
      });
      
      setManagedBikes(loadedBikes);
      console.log('✅ Loaded', loadedBikes.length, 'managed bikes for company:', currentCompany?.name);
      
      updateDebugInfo('bikesLoaded', {
        count: loadedBikes.length,
        bikes: loadedBikes.map(b => ({ id: b.id, regNumber: b.regNumber, model: b.model }))
      });
      
    } catch (error) {
      console.error('❌ Error loading managed bikes:', error);
      setBikesError('Failed to load bikes: ' + error.message);
    } finally {
      setBikesLoading(false);
    }
  };

  // ENHANCED MOTORBIKE RENTAL CREATION
  const createMotorbikeRental = async (reservationData) => {
    try {
      console.log('🚴 Creating motorbike rental from reservation:', reservationData);
      
      const startDate = reservationData.pickupDateTime ? 
        reservationData.pickupDateTime.split('T')[0] : '';
      const endDate = reservationData.dropoffDateTime ? 
        reservationData.dropoffDateTime.split('T')[0] : '';
      
      if (!startDate || !endDate) {
        console.error('❌ Missing pickup or dropoff dates');
        return;
      }
      
      // USE THE HELPER FUNCTION TO FIND BIKE
      const { bike: targetBike, model: bikeModel, regNumber: bikeIdentifier } = 
        findBikeAndGetModel(reservationData.motorbikeNumber, managedBikes);
      
      // Simple rental ID without invoice reference
      const rentalId = `${companyId.slice(-4)}-RES-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const dailyRate = targetBike ? targetBike.dailyRate : getModelRateWithFallback(bikeModel);
      
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const days = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) || 1;
      const rentalCost = dailyRate * days;
      
      // STATUS CALCULATION
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const pickupDate = new Date(startDate);
      pickupDate.setHours(0, 0, 0, 0);
      const dropoffDate = new Date(endDate);
      dropoffDate.setHours(0, 0, 0, 0);
      
      let status;
      if (today < pickupDate) {
        status = "Reserved";
      } else if (today.getTime() === pickupDate.getTime()) {
        status = "Reserved";
      } else if (today > pickupDate && today <= dropoffDate) {
        status = "Active";
      } else if (today > dropoffDate) {
        status = "Overdue";
      } else {
        status = "Reserved";
      }
      
      // RENTAL ENTRY WITH BIKE MODEL
      const rentalEntry = {
        bike: bikeIdentifier,
        bikeId: targetBike ? targetBike.id : null,
        bikeRegNumber: bikeIdentifier,
        bikeModel: bikeModel, // NOW PROPERLY SET
        startDate: startDate,
        endDate: endDate,
        rentalId: rentalId,
        status: status,
        customerDetails: {
          name: reservationData.customerName || "",
          whatsapp: reservationData.whatsappNumber || "",
          localNumber: reservationData.localNumber || "",
          email: reservationData.email || "",
          licenseNumber: reservationData.licenseNumber || ""
        },
        pickupLocation: reservationData.pickupLocation || 'office',
        customPickupLocation: reservationData.customPickupLocation || '',
        dropoffLocation: reservationData.dropoffLocation || 'office',
        customDropoffLocation: reservationData.customDropoffLocation || '',
        baseCost: rentalCost,
        totalCost: rentalCost,
        reservationId: editingReservationId || reservationData.id || 'new',
        createdFromReservation: true,
        companyId: companyId,
        companyName: currentCompany?.name,
        createdBy: currentUser?.id,
        createdByUsername: currentUser?.username,
        createdAt: new Date().toISOString()
      };
      
      console.log('🎯 Creating rental entry with model:', rentalEntry);
      
      // Add to motorbike rentals collection
      if (motorbikeRentalsCollection) {
        await addDoc(motorbikeRentalsCollection, rentalEntry);
        console.log('✅ Motorbike rental created successfully with model:', bikeModel);
      }
      
    } catch (error) {
      console.error("❌ Error creating motorbike rental:", error);
      throw error;
    }
  };

  // SIMPLE SAVE RESERVATION FUNCTION
  const handleSaveReservation = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      console.log('🔧 Saving reservation...');
      
      const reservationId = await saveReservation(reservationData);
      console.log('✅ Reservation saved with ID:', reservationId);
      
      // Handle rental creation for booked reservations
      if (reservationData.availabilityStatus === "booked") {
        try {
          console.log('🔧 Creating rental entry...');
          await createMotorbikeRental({
            ...reservationData,
            id: editingReservationId || reservationId
          });
          console.log('✅ Rental entry processed');
        } catch (rentalError) {
          console.error('⚠️ Rental entry error (continuing with reservation):', rentalError);
        }
      }
      
      // Close form and reset state
      handleClosePopup();
      
      // Show success message
      const isEdit = !!editingReservationId;
      const isCancel = reservationData.availabilityStatus === 'cancel';
      const isBooked = reservationData.availabilityStatus === 'booked';
      
      let message;
      if (isCancel) {
        message = `Reservation cancelled successfully!`;
      } else if (isEdit) {
        message = isBooked 
          ? `Reservation updated and synced with calendar!`
          : `Reservation updated successfully!`;
      } else {
        message = isBooked 
          ? `Reservation created and added to calendar!`
          : `Reservation saved successfully!`;
      }
      
      alert(message);
      
    } catch (error) {
      console.error('❌ Save reservation error:', error);
      
      let userMessage = 'Error saving reservation. ';
      if (error.message.includes('permission')) {
        userMessage += 'You may not have permission to perform this action.';
      } else if (error.message.includes('network') || error.message.includes('Failed to fetch')) {
        userMessage += 'Please check your internet connection and try again.';
      } else if (error.message.includes('not found')) {
        userMessage += 'The reservation could not be found. Please refresh the page.';
      } else {
        userMessage += error.message;
      }
      
      alert(userMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // 🔥 NEW: CLEAN UPLOADED DOCUMENTS FOR FIRESTORE COMPATIBILITY
  const cleanUploadedDocumentsForFirestore = (uploadedDocuments) => {
    if (!uploadedDocuments || typeof uploadedDocuments !== 'object') {
      console.log('⚠️ No uploadedDocuments to clean');
      return null;
    }

    console.log('🧹 Cleaning uploadedDocuments for Firestore...');
    console.log('🧹 Input:', uploadedDocuments);
    
    const cleaned = {};
    let cleanedCount = 0;

    Object.entries(uploadedDocuments).forEach(([docId, docData]) => {
      if (!docData || typeof docData !== 'object') {
        console.log(`⚠️ Skipping invalid document ${docId}`);
        return;
      }

      // Only keep Firestore-safe fields
      cleaned[docId] = {
        documentId: docData.documentId || parseInt(docId, 10),
        documentName: docData.documentName || docData.name || `Document ${docId}`,
        category: docData.category || 'general',
        required: docData.required === true,
        fileName: docData.fileName || docData.name || `file_${docId}`,
        fileSize: typeof docData.fileSize === 'number' ? docData.fileSize : 0,
        fileType: docData.fileType || docData.type || 'application/octet-stream',
        downloadURL: docData.downloadURL || '',
        storagePath: docData.storagePath || '',
        uploadedAt: docData.uploadedAt || new Date().toISOString(),
        status: docData.status || 'uploaded',
        isExisting: docData.isExisting === true || docData.status === 'existing',
        compressed: docData.compressed === true,
        retries: typeof docData.retries === 'number' ? docData.retries : 0
      };

      cleanedCount++;
      console.log(`✅ Cleaned document ${docId}:`, cleaned[docId].fileName);
    });

    console.log('🧹 Cleaned', cleanedCount, 'documents');
    console.log('🧹 Output:', cleaned);
    
    return Object.keys(cleaned).length > 0 ? cleaned : null;
  };

  // 🔥 ENHANCED SAVE TO FIREBASE WITH DOCUMENT CLEANING
  const saveReservation = async (data) => {
    try {
      console.log('💾 saveReservation called');
      console.log('💾 Input data keys:', Object.keys(data));
      console.log('💾 Has uploadedDocuments?', !!data.uploadedDocuments);
      
      if (!reservationsCollection) {
        throw new Error('Cannot access reservations collection');
      }

      if (!companyId) {
        throw new Error('Company context is missing');
      }

      // FIND THE BIKE AND GET ITS MODEL
      let bikeModel = data.bikeModel;
      let bikeRegNumber = data.motorbikeNumber;
      
      if (data.motorbikeNumber && managedBikes.length > 0) {
        const bikeInfo = findBikeAndGetModel(data.motorbikeNumber, managedBikes);
        bikeModel = bikeInfo.model;
        bikeRegNumber = bikeInfo.regNumber;
        
        console.log('🚴 Bike info for reservation:', {
          input: data.motorbikeNumber,
          foundModel: bikeModel,
          foundRegNumber: bikeRegNumber
        });
      }

      // 🔥 CLEAN UPLOADED DOCUMENTS FOR FIRESTORE
      let cleanedUploadedDocuments = null;
      if (data.uploadedDocuments) {
        console.log('📄 ========================================');
        console.log('📄 CLEANING UPLOADED DOCUMENTS');
        console.log('📄 ========================================');
        console.log('📄 Raw uploadedDocuments:', data.uploadedDocuments);
        
        cleanedUploadedDocuments = cleanUploadedDocumentsForFirestore(data.uploadedDocuments);
        
        console.log('📄 Cleaned uploadedDocuments:', cleanedUploadedDocuments);
        console.log('📄 Will save to Firestore:', !!cleanedUploadedDocuments);
        console.log('📄 ========================================');
      } else {
        console.log('⚠️ No uploadedDocuments in data');
      }

      // Prepare data WITH CLEANED UPLOADED DOCUMENTS
      const dataToSave = {
        ...data,
        motorbikeNumber: bikeRegNumber,
        bikeModel: bikeModel,
        uploadedDocuments: cleanedUploadedDocuments, // 🔥 USE CLEANED VERSION
        companyId: companyId,
        companyName: currentCompany?.name,
        createdBy: currentUser?.id,
        createdByUsername: currentUser?.username,
        updatedAt: new Date().toISOString()
      };

      console.log('💾 ========================================');
      console.log('💾 FINAL DATA TO SAVE');
      console.log('💾 ========================================');
      console.log('💾 Has uploadedDocuments?', !!dataToSave.uploadedDocuments);
      console.log('💾 uploadedDocuments keys:', dataToSave.uploadedDocuments ? Object.keys(dataToSave.uploadedDocuments) : 'N/A');
      console.log('💾 All fields being saved:', Object.keys(dataToSave));
      console.log('💾 ========================================');

      let documentId;
      
      if (editingReservationId) {
        // UPDATE existing reservation
        console.log('🔄 Updating reservation:', editingReservationId);
        const docRef = doc(db, getCompanyCollectionPath('reservations'), editingReservationId);
        
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        });
        
        documentId = editingReservationId;
        console.log('✅ Updated reservation with model:', bikeModel);
        console.log('✅ Uploaded documents included:', !!cleanedUploadedDocuments);
        
      } else {
        // CREATE new reservation
        console.log('➕ Creating new reservation');
        console.log('➕ Will save uploadedDocuments:', !!cleanedUploadedDocuments);
        
        const docRef = await addDoc(reservationsCollection, {
          ...dataToSave,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        documentId = docRef.id;
        console.log('✅ Created reservation with ID:', documentId, 'Model:', bikeModel);
        console.log('✅ Uploaded documents included:', !!cleanedUploadedDocuments);
        
        // 🔥 VERIFY THE SAVE
        console.log('🔍 Verifying upload to Firestore...');
        const savedDoc = await getDoc(docRef);
        if (savedDoc.exists()) {
          const savedData = savedDoc.data();
          console.log('✅ Document exists in Firestore');
          console.log('✅ Has uploadedDocuments field?', !!savedData.uploadedDocuments);
          if (savedData.uploadedDocuments) {
            console.log('✅ uploadedDocuments keys:', Object.keys(savedData.uploadedDocuments));
            console.log('✅ SUCCESS! Documents saved to Firestore! 🎉');
          } else {
            console.error('❌ uploadedDocuments field NOT SAVED to Firestore!');
            console.error('❌ This might be a Firestore security rules issue');
          }
        } else {
          console.error('❌ Document not found after creation!');
        }
      }

      return documentId;
      
    } catch (error) {
      console.error('❌ saveReservation error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error('Failed to save: ' + error.message);
    }
  };

  const deleteReservation = async (reservationId) => {
    try {
      setIsSaving(true);
      console.log('🗑️ Deleting reservation for company:', currentCompany?.name);
      
      await deleteDoc(doc(db, getCompanyCollectionPath('reservations'), reservationId));
      
      if (motorbikeRentalsCollection) {
        const rentalQuery = query(motorbikeRentalsCollection, where('reservationId', '==', reservationId));
        const rentalSnapshot = await getDocs(rentalQuery);
        rentalSnapshot.forEach(async (rentalDoc) => {
          await deleteDoc(rentalDoc.ref);
        });
      }

      console.log('✅ Deleted reservation and associated rentals for company:', currentCompany?.name);

    } catch (error) {
      console.error('❌ Error deleting company reservation:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // INQUIRY OPERATIONS
  const saveInquiry = async (inquiryData) => {
    try {
      if (!inquiriesCollection) {
        throw new Error('Cannot access inquiries collection');
      }

      const dataToSave = {
        ...inquiryData,
        companyId: companyId,
        companyName: currentCompany?.name,
        createdBy: currentUser?.id,
        createdByUsername: currentUser?.username,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(inquiriesCollection, dataToSave);
      console.log('✅ Saved inquiry for company:', currentCompany?.name);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error saving company inquiry:', error);
      throw error;
    }
  };

  // INVOICE CREATION CALLBACK - NEW FUNCTION
  const handleInvoiceCreated = (newInvoice) => {
    console.log('🧾 New invoice created from reservation:', newInvoice.invoiceNumber);
    
    setInvoices(prev => [newInvoice, ...prev]);
    
    updateDebugInfo('invoiceCreated', {
      invoiceNumber: newInvoice.invoiceNumber,
      reservationId: newInvoice.reservationId,
      amount: newInvoice.totalAmount,
      timestamp: new Date().toISOString()
    });
    
    const message = `✅ Invoice ${newInvoice.invoiceNumber} created successfully!\n\n` +
                    `Customer: ${newInvoice.customerName}\n` +
                    `Amount: $${parseFloat(newInvoice.totalAmount).toFixed(2)}\n\n` +
                    `The invoice is now available in Invoice Management.`;
    
    console.log('🧾 Invoice creation success:', message.replace(/\n/g, ' '));
  };

  // INVOICE DELETE CALLBACK - NEW FUNCTION
  const handleInvoiceDeleted = (deletedInvoiceId) => {
    console.log('🧾 Invoice deleted:', deletedInvoiceId);
    
    setInvoices(prev => prev.filter(inv => inv.id !== deletedInvoiceId));
    
    updateDebugInfo('invoiceDeleted', {
      invoiceId: deletedInvoiceId,
      timestamp: new Date().toISOString()
    });
  };

  // REAL-TIME LISTENERS WITH INVOICE SUPPORT
  useEffect(() => {
    if (!currentCompany || !currentUser || !companyId) {
      console.log('⚠️ Reservations: Missing company or user context');
      setIsLoading(false);
      return;
    }

    console.log('🔄 Reservations: Setting up real-time listeners for company:', currentCompany.name);

    if (!reservationsCollection || !invoicesCollection) {
      console.error('❌ Cannot set up listeners: Collections not available');
      setIsLoading(false);
      return;
    }

    // Load managed bikes first
    loadManagedBikes();

    // RESERVATIONS LISTENER
    const unsubscribeReservations = onSnapshot(
      query(reservationsCollection, orderBy('createdAt', 'desc')),
      (querySnapshot) => {
        const allReservationsData = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allReservationsData.push({ 
            id: doc.id, 
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt
          });
        });
        
        console.log('🔄 Reservations: Raw data from Firebase:', allReservationsData.length);
        
        const filteredReservations = filterCompanyReservations(allReservationsData, companyId);
        
        setReservations(filteredReservations);
        setDisplayedReservations(filteredReservations);
        setIsLoading(false);
        setLastRefresh(new Date());
        
        console.log('✅ Reservations: Final count after filtering:', filteredReservations.length);
        
        updateDebugInfo('reservationsUpdate', {
          raw: allReservationsData.length,
          filtered: filteredReservations.length,
          timestamp: new Date().toISOString()
        });
        
      },
      (error) => {
        console.error('❌ Error in reservations listener:', error);
        setIsLoading(false);
      }
    );

    // INVOICES LISTENER - NEW
    const unsubscribeInvoices = onSnapshot(
      query(invoicesCollection, orderBy('createdAt', 'desc')),
      (querySnapshot) => {
        const allInvoicesData = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allInvoicesData.push({ 
            id: doc.id, 
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt
          });
        });
        
        console.log('🧾 Invoices: Real-time update:', allInvoicesData.length, 'invoices for', currentCompany.name);
        
        const filteredInvoices = filterCompanyReservations(allInvoicesData, companyId);
        setInvoices(filteredInvoices);
        setInvoicesLoading(false);
        setInvoicesError(null);
        
        updateDebugInfo('invoicesUpdate', {
          raw: allInvoicesData.length,
          filtered: filteredInvoices.length,
          timestamp: new Date().toISOString()
        });
        
      },
      (error) => {
        console.error('❌ Error in invoices listener:', error);
        setInvoicesError('Failed to sync invoices: ' + error.message);
        setInvoicesLoading(false);
      }
    );

    // Set up inquiries listener if collection exists
    let unsubscribeInquiries;
    if (inquiriesCollection) {
      unsubscribeInquiries = onSnapshot(
        query(inquiriesCollection, orderBy('date', 'desc')),
        (querySnapshot) => {
          const allInquiriesData = [];
          querySnapshot.forEach((doc) => {
            allInquiriesData.push({ id: doc.id, ...doc.data() });
          });
          
          const filteredInquiries = filterCompanyReservations(allInquiriesData, companyId);
          setInquiries(filteredInquiries);
          console.log('🔄 Real-time update:', filteredInquiries.length, 'inquiries for', currentCompany.name);
        },
        (error) => {
          console.error('❌ Error in inquiries listener:', error);
        }
      );
    }

    // SET UP MANAGED BIKES REAL-TIME LISTENER
    const unsubscribeBikes = onSnapshot(
      query(managedBikesCollection, where('companyId', '==', companyId)),
      (querySnapshot) => {
        const updatedBikes = [];
        querySnapshot.forEach((doc) => {
          updatedBikes.push({ id: doc.id, ...doc.data() });
        });
        
        setManagedBikes(updatedBikes);
        setBikesLoading(false);
        setBikesError(null);
        console.log('🚴 Real-time update:', updatedBikes.length, 'managed bikes for', currentCompany.name);
        
        updateDebugInfo('bikesUpdate', {
          count: updatedBikes.length,
          timestamp: new Date().toISOString()
        });
      },
      (error) => {
        console.error('❌ Error in managed bikes listener:', error);
        setBikesError('Failed to sync bikes: ' + error.message);
        setBikesLoading(false);
      }
    );

    const handleNewInquiry = (event) => {
      setShowInquiries(true);
    };
    
    window.addEventListener('newInquiry', handleNewInquiry);

    return () => {
      unsubscribeReservations();
      unsubscribeInvoices();
      if (unsubscribeInquiries) unsubscribeInquiries();
      unsubscribeBikes();
      window.removeEventListener('newInquiry', handleNewInquiry);
      console.log('🔄 Reservations: Cleaned up all listeners including invoices for company:', currentCompany.name);
    };
  }, [currentCompany, currentUser, companyId, reservationsCollection, inquiriesCollection, invoicesCollection, managedBikesCollection]);

  // EVENT HANDLERS
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If motorbike number changes, automatically find and set the model
    if (name === 'motorbikeNumber' && value && managedBikes.length > 0) {
      const bikeInfo = findBikeAndGetModel(value, managedBikes);
      setReservationData((prevData) => ({ 
        ...prevData, 
        [name]: value,
        bikeModel: bikeInfo.model // Auto-set the model
      }));
      console.log('🚴 Auto-set bike model:', bikeInfo.model, 'for bike:', value);
    } else {
      setReservationData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleAddReservation = () => {
    setReservationData({
      customerName: '',
      whatsappNumber: '',
      localNumber: '',
      email: '',
      licenseNumber: '',
      pickupDateTime: '',
      dropoffDateTime: '',
      pickupLocation: 'SLBR',
      customPickupLocation: '',
      dropoffLocation: 'SLBR',
      customDropoffLocation: '',
      motorbikeNumber: '',
      bikeModel: '', // NEW: Reset bike model
      availabilityStatus: 'booked',
      rentalAmount: '',
      rentalPaymentStatus: 'paid',
      depositAmount: '$100',
      drivingLicenseStatus: 'applied',
      drivingLicensePaymentStatus: 'paid',
      licensePickupDate: '',
      licensePickupLocation: 'SLBR',
      customLicensePickupLocation: '',
      note: '',
      isInquiry: false,
      inquirySource: '',
      companyId: companyId,
      companyName: currentCompany?.name,
      createdBy: currentUser?.id,
      createdByUsername: currentUser?.username
    });
    setEditIndex(null);
    setEditingReservationId(null);
    setShowPopup(true);
  };
  
  const handleClosePopup = () => {
    setShowPopup(false);
    setEditIndex(null);
    setEditingReservationId(null);
  };

  // EDIT HANDLER
  const handleEditReservation = (reservation, index) => {
    console.log('🔧 Starting edit for reservation:', reservation.id);
    
    try {
      setEditIndex(index);
      setEditingReservationId(reservation.id);
      
      const processedData = {
        customerName: reservation.customerName || '',
        whatsappNumber: reservation.whatsappNumber || '',
        localNumber: reservation.localNumber || '',
        email: reservation.email || '',
        licenseNumber: reservation.licenseNumber || '',
        pickupDateTime: reservation.pickupDateTime || '',
        dropoffDateTime: reservation.dropoffDateTime || '',
        pickupLocation: reservation.pickupLocation || 'SLBR',
        customPickupLocation: reservation.customPickupLocation || '',
        dropoffLocation: reservation.dropoffLocation || 'SLBR',
        customDropoffLocation: reservation.customDropoffLocation || '',
        motorbikeNumber: reservation.motorbikeNumber || '',
        bikeModel: reservation.bikeModel || '', // NEW: Load bike model
        availabilityStatus: reservation.availabilityStatus || 'pending',
        rentalAmount: reservation.rentalAmount || '',
        rentalPaymentStatus: reservation.rentalPaymentStatus || 'not paid',
        depositAmount: reservation.depositAmount || '$100',
        drivingLicenseStatus: reservation.drivingLicenseStatus || 'not applied',
        drivingLicensePaymentStatus: reservation.drivingLicensePaymentStatus || 'not paid',
        licensePickupDate: reservation.licensePickupDate || '',
        licensePickupLocation: reservation.licensePickupLocation || 'SLBR',
        customLicensePickupLocation: reservation.customLicensePickupLocation || '',
        note: reservation.note || '',
        companyId: reservation.companyId,
        companyName: reservation.companyName,
        createdAt: reservation.createdAt,
        createdBy: reservation.createdBy,
        createdByUsername: reservation.createdByUsername,
        isInquiry: reservation.isInquiry || false,
        inquirySource: reservation.inquirySource || '',
        
        // 🔥 ADD-ONS DATA
        selectedAddons: reservation.selectedAddons || [],
        addonCosts: reservation.addonCosts || '0',
        
        // 🔥 PAYMENT METHOD
        paymentMethod: reservation.paymentMethod || 'Cash',
        
        // 🔥 DISCOUNT DATA
        subtotalAmount: reservation.subtotalAmount || '',
        discountAmount: reservation.discountAmount || '0',
        totalAmount: reservation.totalAmount || '',
        discountData: reservation.discountData || null,
        hasDiscount: reservation.hasDiscount || false,
        
        // 🔥 CUSTOM FIELDS (License Type, etc.)
        // Note: Add any additional custom fields from your fieldSettings here
        // Format: fieldName: reservation.fieldName || ''
        License_type: reservation.License_type || '',
        
        // 🔥 CALCULATION DETAILS
        calculationDetails: reservation.calculationDetails || null,
        
        // 🔥 DOCUMENT REQUIREMENTS
        documentRequirements: reservation.documentRequirements || [],
        uploadedDocuments: reservation.uploadedDocuments || null,
        documentValidationStatus: reservation.documentValidationStatus || '',
        documentsStoredInFirebase: reservation.documentsStoredInFirebase || false,
        documentCount: reservation.documentCount || 0
      };
      
      setReservationData(processedData);
      setShowPopup(true);
      
      console.log('✅ ========================================');
      console.log('✅ Edit form opened successfully!');
      console.log('✅ ========================================');
      console.log('✅ Bike Model:', processedData.bikeModel);
      console.log('✅ Documents:', !!processedData.uploadedDocuments, 
        processedData.uploadedDocuments ? Object.keys(processedData.uploadedDocuments) : 'none');
      console.log('✅ Add-ons:', processedData.selectedAddons?.length || 0, 'selected');
      console.log('✅ Payment Method:', processedData.paymentMethod);
      console.log('✅ Deposit Amount:', processedData.depositAmount);
      console.log('✅ License Type:', processedData.License_type || 'not set');
      console.log('✅ Discount:', processedData.hasDiscount ? 'Yes' : 'No');
      console.log('✅ ========================================');
      
    } catch (error) {
      console.error('❌ Error setting up edit:', error);
      alert('Error opening edit form. Please try again.');
    }
  };

  const handleAddReservationFromInquiry = async (inquiryData) => {
    try {
      const newReservation = {
        ...inquiryData,
        availabilityStatus: inquiryData.availabilityStatus || 'pending',
        rentalPaymentStatus: inquiryData.rentalPaymentStatus || 'not paid',
        drivingLicenseStatus: inquiryData.drivingLicenseStatus || 'not applied',
        drivingLicensePaymentStatus: inquiryData.drivingLicensePaymentStatus || 'not paid',
        companyId: companyId,
        companyName: currentCompany?.name,
        createdBy: currentUser?.id,
        createdByUsername: currentUser?.username
      };
      
      await saveReservation(newReservation);
      alert(`Reservation successfully created from ${inquiryData.inquirySource} inquiry for ${currentCompany?.name}!`);
      
    } catch (error) {
      console.error('❌ Error creating reservation from inquiry:', error);
      alert('Error creating reservation from inquiry. Please try again.');
    }
  };

  const handleDeleteReservation = async (reservation) => {
    if (window.confirm(`Are you sure you want to delete this reservation from ${currentCompany?.name}?`)) {
      try {
        await deleteReservation(reservation.id);
        alert('Reservation deleted successfully!');
      } catch (error) {
        console.error('❌ Error deleting company reservation:', error);
        alert('Error deleting reservation. Please try again.');
      }
    }
  };

  const handleFilteredDataChange = (filteredData) => {
    const consistentFilteredData = filterCompanyReservations(filteredData, companyId);
    setDisplayedReservations(consistentFilteredData);
    console.log('🔍 Filter system applied:', consistentFilteredData.length, 'results for company:', currentCompany?.name);
  };

  // FILTER FUNCTIONS
  const handleFilterByLicensePickupDate = (date) => {
    setLicensePickupDate(date);
    if (date) {
      const filtered = reservations.filter(
        (reservation) =>
          reservation.drivingLicensePaymentStatus === "paid" &&
          reservation.licensePickupDate === date &&
          reservation.availabilityStatus !== "cancel"
      );
      
      const companyFiltered = filterCompanyReservations(filtered, companyId);
      setFilteredReservations(companyFiltered);
      console.log('🔍 Filtered', companyFiltered.length, 'license pickups for', currentCompany?.name);
    } else {
      setFilteredReservations([]);
    }
  };

  const handleFilterByBikePickupDate = (date) => {
    setBikePickupDate(date);
    if (date) {
      const filtered = reservations.filter(
        (reservation) =>
          reservation.availabilityStatus === "booked" &&
          reservation.availabilityStatus !== "cancel" &&
          new Date(reservation.pickupDateTime).toLocaleDateString("en-CA") === date
      );
      
      const companyFiltered = filterCompanyReservations(filtered, companyId);
      setFilteredPickupReservations(companyFiltered);
      console.log('🔍 Filtered', companyFiltered.length, 'bike pickups for', currentCompany?.name);
    } else {
      setFilteredPickupReservations([]);
    }
  };
  
  const handleFilterByBikeDropoffDate = (date) => {
    setBikeDropoffDate(date);
    if (date) {
      const filtered = reservations.filter(
        (reservation) =>
          reservation.availabilityStatus === "booked" &&
          reservation.availabilityStatus !== "cancel" &&
          new Date(reservation.dropoffDateTime).toLocaleDateString("en-CA") === date
      );
      
      const companyFiltered = filterCompanyReservations(filtered, companyId);
      setFilteredDropoffReservations(companyFiltered);
      console.log('🔍 Filtered', companyFiltered.length, 'bike dropoffs for', currentCompany?.name);
    } else {
      setFilteredDropoffReservations([]);
    }
  };

  // UI HANDLERS
  const showTooltip = (index) => {
    setTooltipIndex(index);
    setTooltipVisible(true);
  };

  const hideTooltip = () => {
    setTooltipVisible(false);
  };

  const toggleInquiriesSection = () => {
    setShowInquiries(!showInquiries);
  };

  const handleRefreshData = () => {
    setLastRefresh(new Date());
    loadManagedBikes();
    console.log('🔄 Manual refresh triggered');
  };

  // UTILITY FUNCTIONS
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Convert managed bikes to the format expected by filter system
  const filterOptions = {
    bikeModels: managedBikes.map(bike => bike.regNumber || bike.model || bike.id).filter(Boolean),
  };

  // NEW: Conditional rendering for SharedInvoicePreviewPage
  if (currentView === 'invoice-preview' && selectedInvoiceData) {
    return (
      <SharedInvoicePreviewPage
        invoice={selectedInvoiceData.invoice}
        currentUser={selectedInvoiceData.currentUser}
        currentCompany={selectedInvoiceData.currentCompany}
        sourceComponent={selectedInvoiceData.sourceComponent}
        onNavigateBack={handleNavigateBackToReservations}
      />
    );
  }

  // PERMISSION CHECK
  if (!hasPermission('reservations')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-8 sm:py-12">
            <Shield className="mx-auto h-16 w-16 sm:h-20 sm:w-20 text-gray-400 mb-4 sm:mb-6" />
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-300 mb-3 sm:mb-4">Access Denied</h3>
            <p className="text-gray-500 text-base sm:text-lg">You don't have permission to access the reservations system.</p>
            <p className="text-gray-600 text-xs sm:text-sm mt-2">Contact your company administrator for access.</p>
            <div className="mt-4 sm:mt-6 bg-blue-900/30 border border-blue-700 rounded-lg p-3 sm:p-4 max-w-md mx-auto">
              <div className="flex items-center text-blue-300 text-xs sm:text-sm">
                <Building className="mr-2 flex-shrink-0" size={16} />
                <span className="truncate">Company: {currentCompany?.name}</span>
              </div>
              <div className="flex items-center text-blue-300 text-xs sm:text-sm mt-1">
                <User className="mr-2 flex-shrink-0" size={16} />
                <span className="truncate">User: {currentUser?.username} ({currentUser?.role})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LOADING STATE
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-4 sm:p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center px-4">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-3 sm:mb-4"></div>
              <p className="text-white text-base sm:text-lg">Loading Reservations...</p>
              <p className="text-gray-400 text-xs sm:text-sm mt-2">
                {currentCompany ? `Loading data for ${currentCompany.name}` : 'Loading company context...'}
              </p>
              {bikesLoading && (
                <p className="text-gray-500 text-xs mt-1">Loading managed bikes...</p>
              )}
              {invoicesLoading && (
                <p className="text-gray-500 text-xs mt-1">Loading invoices...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MAIN RESERVATIONS INTERFACE - Rest of component remains the same...
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* MAIN HEADER - MOBILE RESPONSIVE */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 md:mb-8 gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white flex flex-wrap items-center gap-2">
            <MotorbikeIcon size={24} className="text-orange-500 sm:w-7 sm:h-7 flex-shrink-0" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600">
              Reservations
            </span>
            <div className="flex items-center text-green-400 text-xs">
              <Lock size={12} className="mr-1" />
              <span className="hidden sm:inline">Company Data</span>
            </div>
            <div className="flex items-center text-blue-400 text-xs">
              <Receipt size={12} className="mr-1" />
              <span className="hidden sm:inline">Bike Models Connected</span>
            </div>
          </h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4">
            <div className="flex items-center text-blue-400 text-xs sm:text-sm">
              <Building size={14} className="mr-1 flex-shrink-0" />
              <span className="truncate max-w-[120px] sm:max-w-none">{currentCompany?.name}</span>
            </div>
            <button
              onClick={handleRefreshData}
              className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-xs"
              title="Refresh data"
            >
              <RefreshCw size={14} />
              <span>Sync</span>
            </button>
            <button
              className="flex items-center gap-1 sm:gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors disabled:opacity-50 text-xs sm:text-sm whitespace-nowrap"
              onClick={handleAddReservation}
              disabled={isSaving}
            >
              <Plus size={16} className="sm:w-5 sm:h-5" /> 
              <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Add Reservation'}</span>
              <span className="sm:hidden">{isSaving ? 'Saving...' : 'Add'}</span>
            </button>
          </div>
        </div>

        {/* SUCCESS BANNER - 🔥 NEW */}
        <div className="mb-4 sm:mb-6 bg-green-900/30 border border-green-700/50 rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1 min-w-0">
              <h4 className="text-green-300 font-semibold text-sm sm:text-base mb-1">
                ✅ Document Upload System Enhanced!
              </h4>
              <p className="text-gray-300 text-xs sm:text-sm">
                Documents now save properly to Firestore with cleaned data structure.
                {managedBikes.length > 0 && (
                  <span className="block mt-1">
                    <strong>{managedBikes.length} bikes</strong> loaded from Firebase managed bikes collection.
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Rest of the component JSX remains exactly the same... */}
        {/* (Statistics, Inquiries, Filter System, Reservation List, etc.) */}
        
        {/* RESERVATION STATISTICS */}
        <div className="mb-4 sm:mb-6">
          <ReservationStatistics 
            reservations={reservations}
            currentUser={currentUser}
            currentCompany={currentCompany}
            showCompanyBreakdown={true}
          />
        </div>
        
        {/* INQUIRIES SECTION */}
        <div className="mb-4 sm:mb-6">
          <div 
            className="flex justify-between items-center bg-gray-800 rounded-t-xl border border-gray-700 p-3 sm:p-4 cursor-pointer"
            onClick={toggleInquiriesSection}
          >
            <h3 className="text-base sm:text-lg font-semibold text-white flex flex-wrap items-center gap-2">
              <Mail className="text-purple-500 flex-shrink-0" size={18} />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500">
                Inquiries
              </span>
              <div className="flex items-center text-green-400 text-xs">
                <Lock size={10} className="mr-1" />
                <span className="hidden sm:inline">Company Isolated</span>
              </div>
              <span className="text-gray-400 text-xs sm:text-sm">({inquiries.length})</span>
            </h3>
            <button className="text-gray-400 hover:text-white flex-shrink-0">
              {showInquiries ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>
          
          {showInquiries && (
            <div className="bg-gray-800 rounded-b-xl border-x border-b border-gray-700 p-3 sm:p-4">
              <InquiryHandler 
                onAddReservation={handleAddReservationFromInquiry}
                inquiries={inquiries}
                onSaveInquiry={saveInquiry}
              />
            </div>
          )}
        </div>

        {/* FILTER SYSTEM */}
        <div className="mb-4 sm:mb-6">
          <FilterSystem 
            data={reservations} 
            onFilteredDataChange={handleFilteredDataChange}
            filterOptions={filterOptions}
          />
        </div>

        {/* MAIN RESERVATIONS LIST */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden mb-4 sm:mb-6 md:mb-8">
          <div className="p-4 sm:p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h3 className="text-lg sm:text-xl font-semibold text-white flex flex-wrap items-center gap-2">
                <MotorbikeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />
                <span>All Reservations</span>
                <div className="flex items-center text-green-400 text-xs">
                  <Lock size={10} className="mr-1" />
                  <span className="truncate max-w-[100px] sm:max-w-none">{currentCompany?.name}</span>
                </div>
                <div className="flex items-center text-blue-400 text-xs">
                  <Receipt size={10} className="mr-1" />
                  <span className="hidden sm:inline">With Models</span>
                </div>
              </h3>
              <div className="text-gray-400 text-xs sm:text-sm">
                {displayedReservations.length} of {reservations.length} total
              </div>
            </div>
          </div>

          <ReservationList
            reservations={displayedReservations}
            emptyMessage={`No reservations match your filters for ${currentCompany?.name}.`}
            hoverColor="hover:bg-gray-700"
            iconColor="text-orange-400"
            onExpandDetails={(reservation) => handleEditReservation(reservation, 0)}
            onDeleteReservation={handleDeleteReservation}
            maxHeight="600px"
            currentUser={currentUser}
            currentCompany={currentCompany}
            getCompanyContext={getCompanyContext}
            showCompanyInfo={false}
            showInvoiceButton={true}
            onInvoiceCreated={handleInvoiceCreated}
            onNavigateToInvoicePreview={handleNavigateToInvoicePreview}
            existingInvoices={invoices.map(inv => inv.reservationId)}
          />
        </div>

        {/* RESERVATION FORM */}
        {showPopup && (
          <ReservationForm
            reservationData={reservationData}
            handleInputChange={handleInputChange}
            handleSaveReservation={handleSaveReservation}
            handleClosePopup={handleClosePopup}
            editIndex={editIndex}
            motorbikes={managedBikes}
            isSaving={isSaving}
            currentUser={currentUser}
            currentCompany={currentCompany}
            getCompanyContext={getCompanyContext}
            dataIsolationActive={true}
            tenantContext={{
              companyId: companyId,
              companyName: currentCompany?.name,
              userId: currentUser?.id,
              username: currentUser?.username,
              userRole: currentUser?.role
            }}
            validateCompanyAccess={(targetCompanyId) => targetCompanyId === companyId}
          />
        )}

        {/* PICKUP AND DROPOFF SECTIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mt-4 sm:mt-6 md:mt-8">
          {/* Pickup Section */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
            <div className="p-4 sm:p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
              <h3 className="text-lg sm:text-xl font-semibold text-white flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
                <MotorbikeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />
                <span>Bike Pickup</span>
                <div className="flex items-center text-green-400 text-xs ml-auto">
                  <Lock size={10} className="mr-1" />
                  <span className="truncate max-w-[100px] sm:max-w-none">{currentCompany?.name}</span>
                </div>
              </h3>
              <input
                type="date"
                value={bikePickupDate}
                onChange={(e) => handleFilterByBikePickupDate(e.target.value)}
                className="w-full p-2 sm:p-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {bikePickupDate && (
              <div className="p-3 sm:p-4 bg-gray-800">
                <div className="mb-3 sm:mb-4 bg-gray-700 rounded-lg p-3 sm:p-4 border-l-4 border-orange-500">
                  <h4 className="text-gray-200 text-xs sm:text-sm">
                    Pickups Scheduled for{" "}
                    <span className="text-orange-400 font-semibold block sm:inline mt-1 sm:mt-0">
                      {formatDate(bikePickupDate)}
                    </span>
                  </h4>
                </div>
                <ReservationList
                  reservations={filteredPickupReservations}
                  emptyMessage="No pickups scheduled"
                  hoverColor="hover:bg-gray-700"
                  iconColor="text-orange-400"
                  onDeleteReservation={handleDeleteReservation}
                  maxHeight="400px"
                  currentUser={currentUser}
                  currentCompany={currentCompany}
                  getCompanyContext={getCompanyContext}
                  showCompanyInfo={false}
                  showInvoiceButton={true}
                  onInvoiceCreated={handleInvoiceCreated}
                  onNavigateToInvoicePreview={handleNavigateToInvoicePreview}
                  existingInvoices={invoices.map(inv => inv.reservationId)}
                />
              </div>
            )}
          </div>

          {/* Dropoff Section */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
            <div className="p-4 sm:p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
              <h3 className="text-lg sm:text-xl font-semibold text-white flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />
                <span>Bike Dropoff</span>
                <div className="flex items-center text-green-400 text-xs ml-auto">
                  <Lock size={10} className="mr-1" />
                  <span className="truncate max-w-[100px] sm:max-w-none">{currentCompany?.name}</span>
                </div>
              </h3>
              <input
                type="date"
                value={bikeDropoffDate}
                onChange={(e) => handleFilterByBikeDropoffDate(e.target.value)}
                className="w-full p-2 sm:p-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {bikeDropoffDate && (
              <div className="p-3 sm:p-4 bg-gray-800">
                <div className="mb-3 sm:mb-4 bg-gray-700 rounded-lg p-3 sm:p-4 border-l-4 border-orange-500">
                  <h4 className="text-gray-200 text-xs sm:text-sm">
                    Dropoffs Scheduled for{" "}
                    <span className="text-orange-400 font-semibold block sm:inline mt-1 sm:mt-0">
                      {formatDate(bikeDropoffDate)}
                    </span>
                  </h4>
                </div>
                <ReservationList
                  reservations={filteredDropoffReservations}
                  emptyMessage="No dropoffs scheduled"
                  hoverColor="hover:bg-gray-700"
                  iconColor="text-orange-400"
                  onDeleteReservation={handleDeleteReservation}
                  maxHeight="400px"
                  currentUser={currentUser}
                  currentCompany={currentCompany}
                  getCompanyContext={getCompanyContext}
                  showCompanyInfo={false}
                  showInvoiceButton={true}
                  onInvoiceCreated={handleInvoiceCreated}
                  onNavigateToInvoicePreview={handleNavigateToInvoicePreview}
                  existingInvoices={invoices.map(inv => inv.reservationId)}
                />
              </div>
            )}
          </div>
        </div>

        {/* LICENSE PICKUP SECTION */}
        <div className="mt-4 sm:mt-6 md:mt-8 bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
          <div className="p-4 sm:p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
            <h3 className="text-lg sm:text-xl font-semibold text-white flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
              <FileCheck className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />
              <span>License Pickup Date</span>
              <div className="flex items-center text-green-400 text-xs ml-auto">
                <Lock size={10} className="mr-1" />
                <span className="truncate max-w-[100px] sm:max-w-none">{currentCompany?.name}</span>
              </div>
            </h3>
            <input
              type="date"
              value={licensePickupDate}
              onChange={(e) => handleFilterByLicensePickupDate(e.target.value)}
              className="w-full p-2 sm:p-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {licensePickupDate && (
            <div className="p-3 sm:p-4 bg-gray-800">
              <div className="mb-3 sm:mb-4 bg-gray-700 rounded-lg p-3 sm:p-4 border-l-4 border-orange-500">
                <h4 className="text-gray-200 text-xs sm:text-sm">
                  License Pickups Scheduled for{" "}
                  <span className="text-orange-400 font-semibold block sm:inline mt-1 sm:mt-0">
                    {formatDate(licensePickupDate)}
                  </span>
                </h4>
              </div>
              <ReservationList
                reservations={filteredReservations}
                emptyMessage="No license pickups scheduled"
                hoverColor="hover:bg-gray-700"
                iconColor="text-orange-400"
                onExpandDetails={handleEditReservation}
                onDeleteReservation={handleDeleteReservation}
                maxHeight="400px"
                currentUser={currentUser}
                currentCompany={currentCompany}
                getCompanyContext={getCompanyContext}
                showCompanyInfo={false}
                showInvoiceButton={true}
                onInvoiceCreated={handleInvoiceCreated}
                onNavigateToInvoicePreview={handleNavigateToInvoicePreview}
                existingInvoices={invoices.map(inv => inv.reservationId)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reservations;